import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Checkbox } from '@mantine/core';
import ProductCard from '../components/ProductCard';
import apiService from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import { isParentSoldOut } from '../utils/soldOut';
import './Products.css';

export default function Products() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryParam = searchParams.get('category');
  const searchQuery = searchParams.get('search');
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.getProductCategories().then(setCategories).catch(() => {});
  }, []);

  const matchedCategory = categories.find(c => c.name === categoryParam || c.name.toLowerCase().replace(/\s+/g, '-') === categoryParam);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getProducts({
        category: matchedCategory?.name || null,
        search: searchQuery,
        lang: t.langCode
      });
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [categoryParam, searchQuery, t.langCode, matchedCategory?.name]);

  const getPageTitle = () => {
    if (searchQuery) {
      return `${t('searchResults', 'Search Results')}: "${searchQuery}"`;
    }
    return matchedCategory?.name || t('ourProducts');
  };

  // const getPageDescription = () => {
  //   if (searchQuery) {
  //     return `${products.length} ${t('productsFound', 'products found')}`;
  //   }
  //   return matchedCategory ? t('paneerIntro') : t('productsIntro');
  // };

  if (loading) {
    return (
      <main className="products-page">
        <div className="products-header">
          <h1>{t('loading', 'Loading...')}</h1>
        </div>
        <div
          className="loading-spinner"
          role="status"
          aria-live="polite"
          aria-label={t('loading', 'Loading...')}
        >
          <div className="spinner" aria-hidden="true"></div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="products-page">
        <div className="products-header">
          <h1>{t('error', 'Error')}</h1>
          <p role="alert" style={{ color: 'red' }}>{error}</p>
        </div>
        <Button
          variant="subtle"
          color="gray"
          onClick={() => window.location.reload()}
        >
          {t('tryAgain', 'Try Again')}
        </Button>
      </main>
    );
  }

  const getTotalStock = (p) => (p.variants || []).reduce((sum, v) => sum + (Number(v.stock_quantity) || 0), 0);
  const hasAnyStock = (p) => !isParentSoldOut(p);

  const sortedProducts = products.slice().sort((a, b) => {
    const aStock = getTotalStock(a);
    const bStock = getTotalStock(b);
    if (aStock === 0 && bStock > 0) return 1;
    if (aStock > 0 && bStock === 0) return -1;
    return 0;
  });

  const filteredProducts = showOutOfStock
    ? sortedProducts
    : sortedProducts.filter(p => hasAnyStock(p));

  return (
    <main className="products-page">
      <div className="products-header">
        <h1>{getPageTitle()}</h1>
        {/* <p>{getPageDescription()}</p> */}
        {searchQuery && (
          <Button
            variant="subtle"
            color="gray"
            onClick={() => navigate('/products')}
          >
            <span aria-hidden="true">{'\u2715 '}</span>
            {t('clearSearch', 'Clear Search')}
          </Button>
        )}
      </div>

      {/* Out-of-stock filter */}
      <fieldset
        className="grid-controls"
        style={{ border: 0, padding: 0, margin: 0 }}
      >
        <legend className="sr-only">{t('productFilters', 'Product filters')}</legend>
        <Checkbox
          checked={showOutOfStock}
          onChange={e => setShowOutOfStock(e.currentTarget.checked)}
          label={t('showOutOfStock', 'Show out-of-stock products')}
        />
      </fieldset>

      {filteredProducts.length === 0 ? (
        <div
          className="no-results"
          role="status"
          aria-live="polite"
        >
          <div className="no-results-icon" aria-hidden="true">🔍</div>
          <h2>{t('noProductsFound', 'No Products Found')}</h2>
          <p>{t('tryDifferentSearch', 'Try searching with different keywords or browse all products.')}</p>
          <Button component="a" href="/products" variant="filled" size="lg">
            {t('viewAllProducts', 'View All Products')}
          </Button>
        </div>
      ) : (
        <div className="products-grid" role="list" aria-label={t('productList', 'Product list')}>
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      )}
    </main>
  );
}